import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import 'echarts-gl';

export const Line3DChart = ({ title, xAxisName, yAxisName, zAxisName, seriesCollection }) => {
    // A ref rather than a hardcoded element id: two of these can be on a page
    // (the drillhole Charts tab and the Analysis page), and getElementById would
    // hand both the same div.
    const container = useRef(null);

    const handleComponentLoaded = () => {
        var main = container.current;
        if (!main) {
            return;
        }
        var xValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                xValues.push(point[0]);
            });
        });
        var yValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                yValues.push(point[1]);
            });
        });
        var zValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                zValues.push(point[2]);
            });
        });

        var min = Math.min(...xValues, ...yValues, ...zValues);
        var max = Math.max(...xValues, ...yValues, ...zValues);
        var range = max - min;
        var xRange = Math.max(...xValues) - Math.min(...xValues);
        var yRange = Math.max(...yValues) - Math.min(...yValues);
        var xAxisMin = Math.min(...xValues) - (range - xRange) / 2;
        var yAxisMin = Math.min(...yValues) - (range - yRange) / 2;
        var xAxisMax = xAxisMin + range;
        var yAxisMax = yAxisMin + range;

        // Build ground plane grid lines at z=0
        var groundLines = [];
        var gridStep = range / 10;

        // Lines along X axis (varying Y)
        for (var y = Math.ceil(yAxisMin / gridStep) * gridStep; y <= yAxisMax; y += gridStep) {
            groundLines.push({
                name: '',
                type: 'line3D',
                data: [[xAxisMin, y, 0], [xAxisMax, y, 0]],
                lineStyle: { width: 1, color: '#bbb', opacity: 0.6 },
                silent: true
            });
        }
        // Lines along Y axis (varying X)
        for (var x = Math.ceil(xAxisMin / gridStep) * gridStep; x <= xAxisMax; x += gridStep) {
            groundLines.push({
                name: '',
                type: 'line3D',
                data: [[x, yAxisMin, 0], [x, yAxisMax, 0]],
                lineStyle: { width: 1, color: '#bbb', opacity: 0.6 },
                silent: true
            });
        }

        // Axis lines through the origin
        var zMin = Math.floor(-range);
        var axisLines = [
            { name: xAxisName, type: 'line3D', data: [[Math.floor(xAxisMin), 0, 0], [Math.ceil(xAxisMax), 0, 0]], lineStyle: { width: 2, color: '#888' }, silent: true },
            { name: yAxisName, type: 'line3D', data: [[0, Math.floor(yAxisMin), 0], [0, Math.ceil(yAxisMax), 0]], lineStyle: { width: 2, color: '#888' }, silent: true },
            { name: zAxisName, type: 'line3D', data: [[0, 0, 0], [0, 0, zMin]], lineStyle: { width: 2, color: '#888' }, silent: true },
        ];

        // series.color pins the colour to the entity rather than to its index,
        // so filtering the collection cannot repaint the surviving series — and
        // a survey keeps the same colour here as in the plan and section views.
        var trajectorySeries = seriesCollection.map((series) => {
            return {
                name: series.name,
                type: 'line3D',
                data: series.points,
                ...(series.color ? { itemStyle: { color: series.color } } : {}),
                lineStyle: {
                    width: 3,
                    ...(series.color ? { color: series.color } : {}),
                    ...series.lineStyle
                }
            }
        });

        // Reuse the existing instance instead of initialising a second one on the
        // same node. Selecting surveys on and off re-runs this on every toggle,
        // and echarts.init would warn and leak a WebGL context each time.
        var chart = echarts.getInstanceByDom(main) || echarts.init(main);
        var option = {
            title: {
                text: title,
                left: 'center'
            },
            tooltip: {},
            toolbox: {
                feature: {
                    saveAsImage: {},
                    restore: {}
                }
            },
            xAxis3D: {
                min: Math.floor(xAxisMin),
                max: Math.ceil(xAxisMax),
                name: xAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            yAxis3D: {
                min: Math.floor(yAxisMin),
                max: Math.ceil(yAxisMax),
                name: yAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            zAxis3D: {
                max: 0,
                min: Math.floor(-range),
                name: zAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            grid3D: {
                show: false,
                viewControl: {
                    autoRotate: false,
                    distance: 250,
                }
            },
            series: [...groundLines, ...axisLines, ...trajectorySeries]
        };
        // notMerge: echarts merges by series index, so without it a trajectory
        // removed from seriesCollection stays on the chart.
        chart.setOption(option, true);
    }

    useEffect(() => {
        handleComponentLoaded();
    }, [seriesCollection]); // eslint-disable-line react-hooks/exhaustive-deps -- handleComponentLoaded only reads seriesCollection, already in deps

    // Release the WebGL context when the tab is left, rather than leaving the
    // instance bound to a detached node.
    useEffect(() => {
        const node = container.current;
        return () => {
            if (node) echarts.getInstanceByDom(node)?.dispose();
        };
    }, []);

    return (
        <div ref={container} style={{ width: '100%', height: 600 }}></div>
    )
}
